import { useRef, useEffect, useState } from "react";
import dayjs from "dayjs";
import generatePDF from "react-to-pdf";

import { Box } from "@mui/material";

import { ReportPages } from "../../../../modules/reporting/Report";
import { uploadFileToStorage } from "../../../../../helpers/firebase";
import { getAccountId } from "../../../../../helpers/accountHelpers";

const SETTLE_DELAY_MS = 3000;

// Bare dayjs(), not dayjs.utc() -- corrected by #2119 after this file
// previously read UTC on the (wrong) premise that every entry here is
// UTC-anchored. `emissions` here is YearlyReportsSection's (and
// NewCustomerForm's) `allEmissions` from helpers/contexts/emissions.js's
// useEmissionsContext -- the same mixed-provenance array #2119/#2122 already
// traced: calcs/recurring output anchors month starts to LOCAL midnight,
// computed fresh by whichever browser is viewing this page and read by this
// same function in that same render, so construction and read always share
// one ambient timezone -- a bare local read is self-consistent regardless of
// what that timezone is. Manually-entered emissions carry a real local
// timestamp a bare local read reproduces correctly. Transaction/
// rollup-month entries (legacy's correctDateForAggregation, and
// reshapeRollupsV2MonthDocsToLegacyShape since #2091) carry a deliberate
// +1-day UTC margin specifically so a bare local read still lands on the
// correct month for any real-world UTC offset. The UTC read this replaces
// was the actual bug: for a positive-UTC-offset viewer, local midnight on
// January 1st parses in UTC as December 31st of the previous year,
// silently dropping a customer's earliest days of a finalized year's report
// into the prior year's PDF -- see #2119 for the full reasoning and
// empirical verification, and #2122 for the same correction applied to
// CloseAccountingButton.js's filterRecurringEmissionsForYear.
export const filterEmissionsForReportYear = (emissions, year) =>
  (emissions || []).filter(({ date }) => {
    const emDate = date?.toDate ? date.toDate() : new Date(date);
    return dayjs(emDate).year() === year;
  });

const ReportRenderer = ({ year, emissions, onUploaded, onError }) => {
  const targetRef = useRef(null);
  const [settled, setSettled] = useState(false);
  const hasStarted = useRef(false);
  const onUploadedRef = useRef(onUploaded);
  const onErrorRef = useRef(onError);
  onUploadedRef.current = onUploaded;
  onErrorRef.current = onError;

  const startDate = new Date(year, 0, 1);
  const endDate = new Date(year, 11, 31);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), SETTLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!settled || hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    const generate = async () => {
      try {
        const pdf = await generatePDF(targetRef, {
          method: "build",
          page: { format: "letter" }
        });
        const blob = pdf.output("blob");
        const accountId = getAccountId();
        const storagePath = `${accountId}/reports/${year}/emissions-report.pdf`;
        const url = await uploadFileToStorage({
          path: storagePath,
          fileObject: blob,
          fileMetadata: { contentType: "application/pdf" },
          hasUserConfirmedUpload: true
        });
        onUploadedRef.current(url);
      } catch (e) {
        onErrorRef.current(e);
      }
    };

    generate();
  }, [settled, year]);

  const yearEmissions = filterEmissionsForReportYear(emissions, year);

  return (
    <Box
      style={{
        position: "absolute",
        left: "-9999px",
        top: 0,
        width: "816px",
        overflow: "hidden"
      }}
    >
      <Box ref={targetRef}>
        <ReportPages
          startDate={startDate}
          endDate={endDate}
          emissions={yearEmissions}
          graphPeriod="year"
          pdfLoading={true}
        />
      </Box>
    </Box>
  );
};

export default ReportRenderer;
